// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/** Jargon → plain-English definitions for click-to-define tooltips. */
export const GLOSSARY: Record<string, string> = {
  checksum:
    'A short fingerprint of a file, computed from its bytes. If two files have the same checksum they are almost certainly identical; change one bit and the checksum changes completely.',
  hash: 'The output of a hash function — a fixed-length fingerprint of whatever data you fed in. "Checksum" and "hash" are used interchangeably here.',
  'sha-256':
    'The most common modern checksum. A 256-bit (64-hex-character) digest from the SHA-2 family, published next to most ISOs, releases and packages.',
  'sha-512':
    'A longer SHA-2 digest (512-bit / 128 hex characters), often used for large release artefacts.',
  'sha-1':
    'An older 160-bit digest. Still used for Git object ids, but broken for security — do not rely on it to prove a file is untampered.',
  md5: 'A fast, 128-bit legacy checksum. Cryptographically broken: fine for spotting accidental corruption, useless against a deliberate attacker.',
  blake3:
    'A very fast modern hash built as a tree, so it parallelises well on large files. Increasingly published alongside SHA-256.',
  blake2b:
    'A fast, strong hash from the BLAKE family, used by some Linux package indexes and tools.',
  'sha3-256':
    'The Keccak-based SHA-3 standard — a structurally different design from SHA-2, useful as a second opinion.',
  crc32:
    'A classic 32-bit checksum used inside ZIP and PNG files. Catches accidental corruption only; trivial to forge.',
  xxhash:
    'An extremely fast non-cryptographic checksum. Great for detecting corruption at speed, never for security.',
  hmac: 'A keyed hash: it mixes a secret key into the digest, so only someone with the key can produce or verify it. Used to authenticate messages and webhooks.',
  digest: 'Another word for the hash value — the fixed-length fingerprint a hash function outputs.',
  integrity:
    'The guarantee that a file is byte-for-byte what was published. A matching checksum proves integrity — that nothing was corrupted or altered in transit.',
  authenticity:
    'The guarantee of who published a file. A checksum alone cannot prove this — if an attacker replaced both the file and its checksum, they would still agree. Digital signatures (GPG, minisign) prove authenticity.',
  streaming:
    'Reading the file in small chunks and feeding each into the hasher, instead of loading the whole thing into memory. It is how Verisum hashes multi-gigabyte files without running out of RAM.',
  wasm: 'WebAssembly — compiled code that runs at near-native speed inside the browser. Verisum uses a WebAssembly hashing engine so it can compute MD5, BLAKE3 and others your browser can’t do on its own.',
  'web worker':
    'A background thread. Verisum hashes files in a worker so the page stays responsive even while crunching a big file.',
  pwa: 'Progressive Web App — once loaded, Verisum is cached by a service worker and keeps working with the network off. Offline is proof nothing is uploaded.',
};

let tooltipEl: HTMLElement | null = null;

/** Wire up click-to-define behaviour for any `.glossary-link[data-term]`. */
export function initGlossary(): void {
  document.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement)?.closest('.glossary-link') as HTMLElement | null;
    if (target) {
      e.preventDefault();
      const term = (target.dataset.term || target.textContent || '').toLowerCase().trim();
      const def = GLOSSARY[term];
      if (def) showTooltip(target, def);
      return;
    }
    if (tooltipEl && !(e.target as HTMLElement)?.closest('.glossary-tip')) hideTooltip();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') hideTooltip();
  });
  window.addEventListener('scroll', hideTooltip, true);
}

function showTooltip(anchor: HTMLElement, text: string): void {
  hideTooltip();
  const tip = document.createElement('div');
  tip.className = 'glossary-tip';
  tip.textContent = text;
  document.body.appendChild(tip);
  const r = anchor.getBoundingClientRect();
  const top = r.bottom + 8;
  let left = r.left;
  const maxLeft = window.innerWidth - tip.offsetWidth - 12;
  if (left > maxLeft) left = Math.max(12, maxLeft);
  tip.style.top = `${top}px`;
  tip.style.left = `${left}px`;
  tooltipEl = tip;
}

function hideTooltip(): void {
  tooltipEl?.remove();
  tooltipEl = null;
}
